/**
 * Wire protocol between a host (the farm owner) and visiting clients. Every
 * inbound message is validated: peers are untrusted and may send anything.
 */
import type { GameAction } from '../game/actions';
import type { GameEvent } from '../game/events';
import { isFlowerId } from '../game/flowers';
import { isValidPlotId } from '../game/grid';
import type { ToolAnimation } from '../game/interaction';
import type { GameState } from '../game/state';
import { isUpgradeId } from '../game/upgrades';
import { normalizeState, parseGameAction } from '../game/validation';
import type { ActorEvent } from '../core/GameController';
import { PLAYER_COLORS, sanitizePlayerName } from '../core/settings';
import { stripControlChars } from '../utils/text';

export const PROTOCOL_VERSION = 1;
export const MAX_CHAT_LENGTH = 120;
export const EMOTES = ['👋', '❤️', '🌸', '😄', '🎉', '✨'] as const;
export type Emote = (typeof EMOTES)[number];

export interface PlayerInfo {
  id: string;
  name: string;
  color: string;
}

export interface PlayerPose {
  x: number;
  z: number;
  rot: number;
  moving: boolean;
  /** One-shot tool animation the player just started. */
  anim?: ToolAnimation;
}

export type ClientMessage =
  | { t: 'hello'; v: number; name: string; color: string }
  | { t: 'action'; action: GameAction }
  | { t: 'pose'; pose: PlayerPose }
  | { t: 'chat'; text: string }
  | { t: 'emote'; emote: Emote };

export type HostMessage =
  | { t: 'welcome'; you: string; state: GameState; players: PlayerInfo[] }
  | { t: 'state'; state: GameState }
  | { t: 'events'; events: ActorEvent[] }
  | { t: 'players'; players: PlayerInfo[] }
  | { t: 'pose'; id: string; pose: PlayerPose }
  | { t: 'chat'; id: string; text: string }
  | { t: 'emote'; id: string; emote: Emote }
  | { t: 'reject'; reason: string }
  | { t: 'kick'; reason: string };

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isId = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 64;

const ANIMATIONS: readonly ToolAnimation[] = ['harvest', 'plant', 'water', 'dig', 'none'];

export function sanitizeChat(text: string): string {
  return stripControlChars(text, ' ').trim().slice(0, MAX_CHAT_LENGTH);
}

function parsePose(raw: unknown): PlayerPose | null {
  if (!isObj(raw)) return null;
  const { x, z, rot, moving, anim } = raw;
  if (!isFiniteNumber(x) || !isFiniteNumber(z) || !isFiniteNumber(rot)) return null;
  if (Math.abs(x) > 200 || Math.abs(z) > 200) return null;
  const pose: PlayerPose = { x, z, rot, moving: moving === true };
  if (ANIMATIONS.includes(anim as ToolAnimation)) pose.anim = anim as ToolAnimation;
  return pose;
}

function parsePlayer(raw: unknown): PlayerInfo | null {
  if (!isObj(raw) || !isId(raw.id) || typeof raw.name !== 'string') return null;
  const name = sanitizePlayerName(raw.name);
  const color = (PLAYER_COLORS as readonly string[]).includes(raw.color as string)
    ? (raw.color as string)
    : PLAYER_COLORS[0];
  return name ? { id: raw.id, name, color } : null;
}

const isEmote = (v: unknown): v is Emote => (EMOTES as readonly unknown[]).includes(v);

/** Validates a game event coming from a host so FX code never sees garbage. */
export function parseEvent(raw: unknown): GameEvent | null {
  if (!isObj(raw) || typeof raw.type !== 'string') return null;
  const e = raw;
  const plot = isValidPlotId(e.plotId);
  switch (e.type) {
    case 'planted':
      return plot && isFlowerId(e.species)
        ? { type: 'planted', plotId: e.plotId as number, species: e.species }
        : null;
    case 'watered':
      return Array.isArray(e.plotIds) && e.plotIds.every(isValidPlotId)
        ? { type: 'watered', plotIds: (e.plotIds as number[]).slice(0, 9) }
        : null;
    case 'harvested':
      return plot && isFlowerId(e.species)
        ? {
            type: 'harvested',
            plotId: e.plotId as number,
            species: e.species,
            golden: e.golden === true,
            seedsDropped: isFiniteNumber(e.seedsDropped)
              ? Math.min(9, Math.max(0, Math.floor(e.seedsDropped)))
              : 0,
            hybrid: isFlowerId(e.hybrid) ? e.hybrid : null,
          }
        : null;
    case 'bloomed':
      return plot && isFlowerId(e.species)
        ? {
            type: 'bloomed',
            plotId: e.plotId as number,
            species: e.species,
            golden: e.golden === true,
          }
        : null;
    case 'dug':
      return plot ? { type: 'dug', plotId: e.plotId as number } : null;
    case 'plotUnlocked':
      return plot && isFiniteNumber(e.cost)
        ? { type: 'plotUnlocked', plotId: e.plotId as number, cost: e.cost }
        : null;
    case 'seedsBought':
      return isFlowerId(e.species) && isFiniteNumber(e.qty) && isFiniteNumber(e.cost)
        ? { type: 'seedsBought', species: e.species, qty: e.qty, cost: e.cost }
        : null;
    case 'sold':
      return isFlowerId(e.species) && isFiniteNumber(e.qty) && isFiniteNumber(e.earned)
        ? {
            type: 'sold',
            species: e.species,
            golden: e.golden === true,
            qty: e.qty,
            earned: e.earned,
          }
        : null;
    case 'upgradeBought':
      return isUpgradeId(e.upgrade) && isFiniteNumber(e.level)
        ? { type: 'upgradeBought', upgrade: e.upgrade, level: e.level }
        : null;
    case 'orderDelivered':
      return isFiniteNumber(e.orderId) &&
        isFiniteNumber(e.customer) &&
        isFiniteNumber(e.reward) &&
        isFiniteNumber(e.xp)
        ? {
            type: 'orderDelivered',
            orderId: e.orderId,
            customer: e.customer,
            reward: e.reward,
            xp: e.xp,
          }
        : null;
    case 'orderDiscarded':
      return isFiniteNumber(e.orderId) ? { type: 'orderDiscarded', orderId: e.orderId } : null;
    case 'newOrder':
      return isFiniteNumber(e.orderId) && isFiniteNumber(e.customer)
        ? { type: 'newOrder', orderId: e.orderId, customer: e.customer }
        : null;
    case 'discovered':
      return isFlowerId(e.species) ? { type: 'discovered', species: e.species } : null;
    case 'levelUp':
      return isFiniteNumber(e.level) ? { type: 'levelUp', level: e.level } : null;
    case 'newDay':
      return isFiniteNumber(e.day) && isFlowerId(e.featured)
        ? { type: 'newDay', day: e.day, featured: e.featured }
        : null;
    case 'rainStart':
    case 'rainStop':
    case 'sprinklers':
      return { type: e.type };
    case 'slept':
      return isFiniteNumber(e.seconds) && isFiniteNumber(e.bloomed)
        ? { type: 'slept', seconds: e.seconds, bloomed: e.bloomed }
        : null;
    case 'farmRenamed':
      return typeof e.name === 'string' ? { type: 'farmRenamed', name: e.name.slice(0, 24) } : null;
    default:
      return null;
  }
}

export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (!isObj(raw)) return null;
  switch (raw.t) {
    case 'hello': {
      const name = typeof raw.name === 'string' ? sanitizePlayerName(raw.name) : '';
      if (!isFiniteNumber(raw.v) || !name) return null;
      const color = (PLAYER_COLORS as readonly string[]).includes(raw.color as string)
        ? (raw.color as string)
        : PLAYER_COLORS[0];
      return { t: 'hello', v: raw.v, name, color };
    }
    case 'action': {
      const action = parseGameAction(raw.action);
      return action ? { t: 'action', action } : null;
    }
    case 'pose': {
      const pose = parsePose(raw.pose);
      return pose ? { t: 'pose', pose } : null;
    }
    case 'chat': {
      const text = typeof raw.text === 'string' ? sanitizeChat(raw.text) : '';
      return text ? { t: 'chat', text } : null;
    }
    case 'emote':
      return isEmote(raw.emote) ? { t: 'emote', emote: raw.emote } : null;
    default:
      return null;
  }
}

export function parseHostMessage(raw: unknown): HostMessage | null {
  if (!isObj(raw)) return null;
  switch (raw.t) {
    case 'welcome': {
      const state = normalizeState(raw.state);
      if (!state || !isId(raw.you) || !Array.isArray(raw.players)) return null;
      const players = raw.players.map(parsePlayer).filter((p): p is PlayerInfo => p !== null);
      return { t: 'welcome', you: raw.you, state, players };
    }
    case 'state': {
      const state = normalizeState(raw.state);
      return state ? { t: 'state', state } : null;
    }
    case 'events': {
      if (!Array.isArray(raw.events)) return null;
      const events: ActorEvent[] = [];
      for (const item of raw.events.slice(0, 100)) {
        if (!isObj(item) || !isId(item.actor)) continue;
        const event = parseEvent(item.event);
        if (event) events.push({ actor: item.actor, event });
      }
      return { t: 'events', events };
    }
    case 'players': {
      if (!Array.isArray(raw.players)) return null;
      return {
        t: 'players',
        players: raw.players.map(parsePlayer).filter((p): p is PlayerInfo => p !== null),
      };
    }
    case 'pose': {
      const pose = parsePose(raw.pose);
      return pose && isId(raw.id) ? { t: 'pose', id: raw.id, pose } : null;
    }
    case 'chat': {
      const text = typeof raw.text === 'string' ? sanitizeChat(raw.text) : '';
      return text && isId(raw.id) ? { t: 'chat', id: raw.id, text } : null;
    }
    case 'emote':
      return isId(raw.id) && isEmote(raw.emote)
        ? { t: 'emote', id: raw.id, emote: raw.emote }
        : null;
    case 'reject':
    case 'kick':
      return typeof raw.reason === 'string' ? { t: raw.t, reason: raw.reason.slice(0, 200) } : null;
    default:
      return null;
  }
}
